// src/components/admin/CoreAIAgent.jsx
// Core AI agent page under Core Module: the admin types an instruction ("Change business hours for Christmas week")
// and the agent carries it out across Core Management. Sensitive actions (price changes, deactivations)
// wait for human approval before executing.
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { CoreAgentService } from '../../api/backendAiService';
import DailyTokenCounter from './DailyTokenCounter';
import '../../styles/core-ai-agent.css';

const EXAMPLES = [
  'What are our current business hours?',
  'Close the salon 1 hour early every day during Christmas week.',
  'List all active services and their prices.',
  'Mark the shop as closed on New Year\'s Day.',
  'Show me all staff members and which services they offer.',
  'Update the description for the Wash and Blow service.',
  'What is our salon\'s current contact number?',
];

const APPROVAL_RULES = [
  'Changing a service or product price',
  'Deactivating or deleting a service, product or staff member',
  'Changing staff account settings',
];

function CoreAIAgent({ salonId, onNavigate }) {
  const [messages, setMessages] = useState([{
    id: 'welcome',
    type: 'ai',
    content: "Tell me what you'd like done in Core Management — for example updating business hours, modifying services or products, or checking staff details. I'll ask for any missing details before making changes, and anything sensitive waits for your approval.",
    timestamp: new Date()
  }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [approvals, setApprovals] = useState({});
  const serviceRef = useRef(null);
  const tokenCounterRef = useRef(null);
  const messagesEndRef = useRef(null);
  const unreportedOutcomes = useRef([]);

  useEffect(() => {
    if (salonId) serviceRef.current = new CoreAgentService(salonId);
  }, [salonId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isLoading]);

  const addMessage = (msg) =>
    setMessages(prev => [...prev, { id: `${Date.now()}-${Math.random()}`, timestamp: new Date(), ...msg }]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading || limitReached || !serviceRef.current) return;

    addMessage({ type: 'user', content: text });
    setInput('');
    setIsLoading(true);

    const outcomes = unreportedOutcomes.current;
    unreportedOutcomes.current = [];
    const outgoing = outcomes.length
      ? `[Update since your last reply: ${outcomes.join(' ')}]\n\n${text}`
      : text;

    try {
      const res = await serviceRef.current.sendMessage(outgoing);
      setApprovals(prev => {
        const next = { ...prev };
        res.pendingActions.forEach(p => { next[p.actionId] = { state: 'pending' }; });
        return next;
      });
      addMessage({ type: 'ai', content: res.message, actions: res.actions, pendingActions: res.pendingActions });
      if (res.dailyUsage) tokenCounterRef.current?.update(res.dailyUsage);
    } catch (error) {
      console.error('Core AI agent error:', error);
      unreportedOutcomes.current = [...outcomes, ...unreportedOutcomes.current];
      const content = error?.status === 429
        ? 'You have reached the daily AI usage limit. Core AI is available again after midnight.'
        : error?.status === 403
          ? (error.message || 'Core AI is not available for your account.')
          : "Something went wrong. Please check the relevant section in Core Management before trying again — some changes may already have been made.";
      addMessage({ type: 'system', content });
    } finally {
      setIsLoading(false);
      tokenCounterRef.current?.refresh();
    }
  };

  const handleDecision = async (action, decision) => {
    setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'working' } }));
    try {
      const res = await serviceRef.current.resolveAction(action.actionId, decision);
      if (res.declined) {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'declined', message: 'Declined — nothing was changed.' } }));
        unreportedOutcomes.current.push(`The admin DECLINED "${action.title}", so it was not done.`);
      } else if (res.success) {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'done', message: res.message } }));
        unreportedOutcomes.current.push(`The admin APPROVED "${action.title}" and it was completed: ${res.message}`);
      } else {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'failed', message: res.message } }));
        unreportedOutcomes.current.push(`The admin approved "${action.title}" but it failed: ${res.message}`);
      }
    } catch (error) {
      setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'failed', message: error.message } }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (t) => t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const renderApprovalCard = (p) => {
    const status = approvals[p.actionId] || { state: 'pending' };
    return (
      <div key={p.actionId} className={`bai-approval-card bai-approval-card--${status.state}`}>
        <div className="bai-approval-head">
          <span className="bai-approval-badge">Needs your approval</span>
          <strong>{p.title}</strong>
        </div>
        {p.lines?.map((line, i) => <div key={i} className="bai-approval-line">{line}</div>)}
        <div className="bai-approval-reason">{p.reason}</div>
        {(status.state === 'pending' || status.state === 'working') && (
          <div className="bai-approval-actions">
            <button className="bai-btn bai-btn--approve" disabled={status.state === 'working'} onClick={() => handleDecision(p, 'approve')}>
              {status.state === 'working' ? 'Working…' : 'Approve'}
            </button>
            <button className="bai-btn bai-btn--decline" disabled={status.state === 'working'} onClick={() => handleDecision(p, 'decline')}>
              Decline
            </button>
          </div>
        )}
        {status.state === 'done' && <div className="bai-approval-result bai-approval-result--ok">✓ {status.message}</div>}
        {status.state === 'declined' && <div className="bai-approval-result">{status.message}</div>}
        {status.state === 'failed' && <div className="bai-approval-result bai-approval-result--err">Not done: {status.message}</div>}
      </div>
    );
  };

  return (
    <div className="ai-tab-container core-ai-agent">
      <div className="ai-tab-header">
        <h2>Core AI</h2>
        <p>Type an instruction and the assistant will carry it out across Core Management — business hours, services, products, staff and contact info.</p>
      </div>

      <DailyTokenCounter ref={tokenCounterRef} salonId={salonId} onLimitReached={setLimitReached} />

      <div className="ai-interaction-panel">
        <div className="ai-prompt-examples">
          <h3>Try an instruction</h3>
          <div className="example-prompts">
            {EXAMPLES.map(ex => (
              <div key={ex} className="prompt-card" onClick={() => setInput(ex)}>"{ex}"</div>
            ))}
          </div>
          <div className="cai-rules">
            <h4>Always asks for your approval</h4>
            <ul>{APPROVAL_RULES.map(r => <li key={r}>{r}</li>)}</ul>
            <p>Branding, images and typography are managed in their respective tabs.</p>
          </div>
          {onNavigate && (
            <div className="cai-shortcuts">
              <h4>Quick navigation</h4>
              <button className="cai-link" onClick={() => onNavigate('core', 'hours')}>Business Hours →</button>
              <button className="cai-link" onClick={() => onNavigate('core', 'services')}>Services →</button>
              <button className="cai-link" onClick={() => onNavigate('core', 'staff')}>Staff →</button>
            </div>
          )}
        </div>

        <div className="ai-chat-interface">
          <div className="chat-messages">
            {messages.map(m => (
              <div key={m.id} className={`message ${m.type}-message`}>
                {m.type === 'ai' && <div className="ai-avatar">🤖</div>}
                {m.type === 'user' && <div className="user-avatar">👤</div>}
                {m.type === 'system' && <div className="system-avatar">ℹ️</div>}
                <div className="message-content">
                  <div className="message-text"><ReactMarkdown>{m.content}</ReactMarkdown></div>
                  {m.actions?.length > 0 && (
                    <div className="bai-done-list">
                      {m.actions.map((a, i) => (
                        <div key={i} className="bai-done-item">
                          <span className="bai-done-check">✓</span>
                          <span>{a.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {m.pendingActions?.map(renderApprovalCard)}
                  <div className="message-time">{formatTime(m.timestamp)}</div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message ai-message">
                <div className="ai-avatar">🤖</div>
                <div className="message-content">
                  <div className="typing-indicator"><span></span><span></span><span></span></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-area">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={limitReached ? 'Daily AI usage limit reached. Resets at midnight.' : 'e.g. Close the salon 1 hour early every day during Christmas week (Enter to send, Shift+Enter for new line)'}
              rows="3"
              disabled={isLoading || limitReached}
            />
            <div className="input-actions">
              <span className="bai-hint">Changes apply immediately to your Core settings.</span>
              <button className="send-btn" onClick={handleSend} disabled={isLoading || !input.trim() || limitReached}>
                {isLoading ? 'Working…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoreAIAgent;
